import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { SWRConfig } from "swr";
import { AppShell } from "@/components/layout/AppShell";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { ConfirmProvider } from "@/components/ui/Confirm";
import { ToastProvider } from "@/components/ui/Toast";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { swrDefaults } from "@/lib/useApi";
import { RedirectIfAuthed, RequireAuth } from "@/routes/RequireAuth";
import { Assessments } from "@/routes/app/Assessments";
import { Companies } from "@/routes/app/Companies";
import { Modules } from "@/routes/app/Modules";
import { Overview } from "@/routes/app/Overview";
import { Profile } from "@/routes/app/Profile";
import { Reports } from "@/routes/app/Reports";
import { Schedule } from "@/routes/app/Schedule";
import { Sites } from "@/routes/app/Sites";
import { Team } from "@/routes/app/Team";
import { VisitDetail } from "@/routes/app/VisitDetail";
import { Visits } from "@/routes/app/Visits";
import { Company } from "@/routes/marketing/Company";
import { DeleteAccount } from "@/routes/marketing/DeleteAccount";
import { Home } from "@/routes/marketing/Home";
import { Pricing } from "@/routes/marketing/Pricing";
import { Product } from "@/routes/marketing/Product";
import { Solutions } from "@/routes/marketing/Solutions";
import { AuthCallback } from "@/routes/public/AuthCallback";
import { ForgotPassword } from "@/routes/public/ForgotPassword";
import { Login } from "@/routes/public/Login";
import { Onboarding } from "@/routes/public/Onboarding";
import { ResetPassword } from "@/routes/public/ResetPassword";
import { SignUp } from "@/routes/public/SignUp";

/**
 * Provider order matters:
 *   ThemeProvider   no dependencies; must wrap everything that renders
 *   SWRConfig       needs no auth, but every fetch it makes does
 *   AuthProvider    owns the session; RequireAuth reads it
 *   BrowserRouter   so guards can use the auth context
 *   ToastProvider   outside Confirm, so the handler that ran after a
 *                   confirmation can report what it did. It renders into the
 *                   top layer via a popover rather than by z-index, because
 *                   `Modal` is a `<dialog>` and nothing stacks above one of
 *                   those - see `Toast.tsx`
 *   ConfirmProvider innermost: it renders a dialog over whatever is on screen,
 *                   and every caller of `useConfirm` is inside the router
 *
 * ── Three shells, deliberately ──────────────────────────────────────────────
 * `MarketingShell`  top navigation, wide bands, animation. For browsing.
 * `AuthLayout`      one form and one way back. No navigation at all, because
 *                   navigation on a sign-in page is an invitation to leave it.
 * `AppShell`        a rail that stays put while dense content changes under it.
 *
 * They share the design tokens and nothing else. A single layout stretched
 * across all three would compromise each one - a sidebar wastes a quarter of a
 * landing page's fold, and a top-nav console makes every page a fresh hunt.
 */
export function App() {
  return (
    <ThemeProvider>
      <SWRConfig value={swrDefaults}>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <ConfirmProvider>
                <Routes>
                  {/* ── Marketing ──────────────────────────────────────────── */}
                  <Route element={<MarketingShell />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/product" element={<Product />} />
                    <Route path="/solutions" element={<Solutions />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/company" element={<Company />} />
                    <Route path="/delete-account" element={<DeleteAccount />} />
                  </Route>

                  {/* ── Auth ───────────────────────────────────────────────── */}
                  <Route
                    path="/login"
                    element={
                      <RedirectIfAuthed>
                        <Login />
                      </RedirectIfAuthed>
                    }
                  />
                  <Route
                    path="/signup"
                    element={
                      <RedirectIfAuthed>
                        <SignUp />
                      </RedirectIfAuthed>
                    }
                  />
                  <Route path="/forgot-password" element={<ForgotPassword />} />

                  {/* Deliberately NOT behind RedirectIfAuthed. The reset link
                    produces a valid session, so redirecting authenticated users
                    away would make the page unreachable by the only people who
                    ever need it. */}
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Where an INVITED account is first activated. An invited user is
                    created with no password, so this link is the only way in for
                    them. Self sign-ups set their password up front and go to
                    /onboarding instead. */}
                  <Route path="/accept-invite" element={<ResetPassword mode="invite" />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />

                  {/* Signed in with no workspace: self sign-up, step two. Not under
                    RequireAuth - that guard's whole job is to send people HERE,
                    and nesting it would be a redirect loop. The screen does its
                    own status check. */}
                  <Route path="/onboarding" element={<Onboarding />} />

                  {/* ── Console ────────────────────────────────────────────── */}
                  <Route element={<RequireAuth />}>
                    <Route path="/app" element={<AppShell />}>
                      <Route index element={<Overview />} />
                      <Route path="schedule" element={<Schedule />} />
                      <Route path="visits" element={<Visits />} />
                      {/* A page rather than a drawer: there is far too much on
                        it for a 28rem panel once the assessment record is
                        there, and a URL is what makes "look at this visit" a
                        link rather than a set of directions. */}
                      <Route path="visits/:id" element={<VisitDetail />} />
                      <Route path="assessments" element={<Assessments />} />
                      <Route path="reports" element={<Reports />} />
                      <Route path="sites" element={<Sites />} />
                      <Route path="team" element={<Team />} />
                      <Route path="companies" element={<Companies />} />
                      {/* The module and its sub-view ride in the search params, the way
                        the account page's sections do. */}
                      <Route path="modules" element={<Modules />} />
                      {/* Reached from the rail's account block. Not in `NAV`:
                        it is the signed-in person's own account rather than a
                        destination in the product. */}
                      <Route path="profile" element={<Profile />} />
                    </Route>
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </ConfirmProvider>
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </SWRConfig>
    </ThemeProvider>
  );
}
